import * as React from 'react';

import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useDebounceFn, useMemoizedFn } from 'ahooks';
import {
  Alert,
  Button,
  Col,
  Descriptions,
  Divider,
  Flex,
  Form,
  Image,
  Row,
  Space,
  Tag,
  Typography,
} from 'antd';

import {
  AntdRouterExternalLink,
  CheckCircleOutlinedIcon,
  CloseCircleOutlinedIcon,
  DecimalInputNumber,
  DeleteOutlinedIcon,
  IntegerInputNumber,
  PictureOutlinedIcon,
  PrimitiveList,
  PrimitiveListItem,
  PrimitiveListItemMeta,
  UndoOutlinedIcon,
} from 'components';
import { usePurchaseAction, usePurchaseValue } from 'context';
import { useMediaQuery } from 'hooks';
import {
  cn,
  dateTimeFormat,
  FILE,
  generateDownloadFile,
  generatePurchaseProductInvoice,
  numberFormat,
  PATHNAME,
  randomId,
  UNIT,
} from 'utils';

const { PRICE } = UNIT;
const {
  IMAGE: {
    SIZE: { WIDTH: IMAGE_SIZE_WIDTH, HEIGHT: IMAGE_SIZE_HEIGHT },
  },
} = FILE;
const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    PRODUCTS: { BASE_URL: PRODUCTS_BASE_URL, UPDATE_URL: PRODUCTS_UPDATE_URL },
    PAYMENT_GATEWAYS: {
      BASE_URL: PAYMENT_GATEWAYS_BASE_URL,
      UPDATE_URL: PAYMENT_GATEWAYS_UPDATE_URL,
    },
    DISCOUNT_CODES: {
      BASE_URL: DISCOUNT_CODES_BASE_URL,
      UPDATE_URL: DISCOUNT_CODES_UPDATE_URL,
    },
  },
} = PATHNAME;

type FieldType = Partial<Record<string, string | number>>;

const PurchaseBaseInfo = () => {
  const { lg: matchesLg } = useMediaQuery();
  const {
    discount,
    gatewayDiscount,
    deliveryPrice,
    createdDate,
    lastModifiedDate,
    discountCode,
    transaction,
    paperInvoice,
    description,
    purchaseProducts = [],
  } = usePurchaseValue();
  const action = usePurchaseAction();

  const { id: discountCodeId, code: discountCodeValue } = discountCode ?? {};
  const { paymentGateway } = transaction ?? {};
  const { id: paymentGatewayId, title: paymentGatewayTitle } =
    paymentGateway ?? {};

  const { run: debounceUpdatePurchaseProducts } = useDebounceFn(
    (id: string, value: number) => {
      const updatedPurchaseProducts = purchaseProducts.map(purchaseProduct => {
        if (id === purchaseProduct.id) {
          const { price } = generatePurchaseProductInvoice(
            purchaseProduct,
            value,
          );

          return {
            ...purchaseProduct,
            amount: value,
            updatedPrice: price,
          };
        }

        return purchaseProduct;
      });

      action({
        type: 'UPDATE_PURCHASE_PRODUCTS',
        payload: {
          purchaseProducts: updatedPurchaseProducts,
        },
      });
    },
  );
  const calculateTotalProductsPrice = useMemoizedFn((value: number) => {
    if (!value) return 0;

    const deliveryPriceValue = deliveryPrice || 0;
    const discountValue = discount || 0;
    const gatewayDiscountValue = gatewayDiscount || 0;

    return value + deliveryPriceValue - discountValue - gatewayDiscountValue;
  });
  const hasModifiedProductPrice = useMemoizedFn(
    (
      initialPrice: number | undefined,
      finalPrice: number | undefined,
    ): initialPrice is number => {
      const initialPriceValue = initialPrice || 0;
      const finalPriceValue = finalPrice || 0;

      return Math.abs(initialPriceValue - finalPriceValue) > 0;
    },
  );

  const {
    originalPrice: purchaseProductsOriginalPrice,
    price: purchaseProductsPrice,
  } = generatePurchaseProductInvoice(purchaseProducts, 'amount');
  const {
    originalPrice: initialPurchaseProductsOriginalPrice,
    price: initialPurchaseProductsPrice,
  } = generatePurchaseProductInvoice(purchaseProducts, 'maxAmount');
  const purchaseProductsDiscount =
    purchaseProductsOriginalPrice - purchaseProductsPrice;
  const totalProductsPrice = calculateTotalProductsPrice(purchaseProductsPrice);
  const initialTotalProductsPrice = calculateTotalProductsPrice(
    initialPurchaseProductsPrice,
  );

  return (
    <Row>
      <Col span={24}>
        <Flex vertical>
          <Typography.Title level={4} className='mb-5 text-lg'>
            جزئیات محصول‌ها
          </Typography.Title>
          <PrimitiveList
            className='max-h-[38rem] overflow-auto'
            dataSource={purchaseProducts}
            renderItem={({
              id,
              productName,
              product,
              isSubProductDeleted,
              subProductAttributes,
              packagingInfo,
              price,
              amount,
              maxAmount,
              updatedPrice,
              freeShipping,
              expressShipping,
            }) => {
              const { id: productId, bannerId: productBannerId } =
                product ?? {};
              const isRemovedFromPurchase = amount === 0;
              const {
                packagingModel: { label: packagingModelLabel },
                measurementUnit: { name: measurementUnitName },
              } = packagingInfo ?? {};

              const pricesDifference = updatedPrice - price;
              const hasPricesDifference = pricesDifference !== 0;
              const hasSubProductAttributes = subProductAttributes.length > 0;
              const hasMultipleAmount = maxAmount > 1;

              return (
                <PrimitiveListItem className='my-5 first:mt-0 last:mb-0'>
                  <PrimitiveListItemMeta
                    title={
                      <Row>
                        <Col
                          span={24}
                          sm={10}
                          md={5}
                          className={cn(
                            'sm:pe-3',
                            isSubProductDeleted && 'opacity-60 grayscale',
                          )}
                        >
                          {productBannerId ? (
                            <Image
                              className='mx-auto h-full w-auto'
                              rootClassName='size-full max-h-48 border sm:max-md:max-h-full'
                              src={generateDownloadFile(
                                'Product',
                                productBannerId,
                                IMAGE_SIZE_WIDTH,
                                IMAGE_SIZE_HEIGHT,
                              )}
                              alt={productName}
                            />
                          ) : (
                            <Flex
                              align='center'
                              justify='center'
                              className='size-full min-h-32 border bg-[--ant-color-fill-quaternary] text-4xl text-[--ant-color-text-quaternary]'
                            >
                              <PictureOutlinedIcon />
                            </Flex>
                          )}
                        </Col>
                        <Col span={24} sm={14} md={19} className='sm:ps-3'>
                          <Row gutter={[14, 14]}>
                            <React.Fragment>
                              {isSubProductDeleted ? (
                                <Col span={24}>
                                  <Alert
                                    showIcon
                                    type={
                                      isRemovedFromPurchase
                                        ? 'warning'
                                        : 'error'
                                    }
                                    message={
                                      isRemovedFromPurchase
                                        ? 'این محصول با ذخیره تغییرات از سفارش حذف می‌شود.'
                                        : 'مدل این محصول حذف شده است. تعداد آن قابل تغییر نیست، اما می‌توانید آن را به‌طور کامل از سفارش حذف کنید.'
                                    }
                                    action={
                                      isRemovedFromPurchase ? (
                                        <Button
                                          size='small'
                                          icon={<UndoOutlinedIcon />}
                                          onClick={() => {
                                            debounceUpdatePurchaseProducts(
                                              id,
                                              maxAmount,
                                            );
                                          }}
                                        >
                                          بازگردانی
                                        </Button>
                                      ) : (
                                        <Button
                                          danger
                                          size='small'
                                          icon={<DeleteOutlinedIcon />}
                                          onClick={() => {
                                            debounceUpdatePurchaseProducts(
                                              id,
                                              0,
                                            );
                                          }}
                                        >
                                          حذف از سفارش
                                        </Button>
                                      )
                                    }
                                  />
                                </Col>
                              ) : null}
                            </React.Fragment>
                            <Col span={24}>
                              <Flex wrap className='gap-x-0.5 gap-y-2'>
                                {productId ? (
                                  <AntdRouterExternalLink
                                    href={`${PRODUCTS_BASE_URL}${PRODUCTS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${productId}${DEFAULT_SLUG_SEPARATOR}`}
                                  >
                                    <bdi>{productName}</bdi>
                                  </AntdRouterExternalLink>
                                ) : (
                                  <Typography.Text delete type='secondary'>
                                    <bdi>{productName}</bdi>
                                  </Typography.Text>
                                )}
                                <React.Fragment>
                                  {hasSubProductAttributes
                                    ? subProductAttributes.map(
                                        ({
                                          identifier,
                                          name,
                                          selectedValues,
                                        }) => (
                                          <Typography.Text
                                            key={identifier}
                                            code={true}
                                            className='*:font-[unset]'
                                          >
                                            <span className='me-1 text-[--ant-color-text-description]'>
                                              {name}:
                                            </span>
                                            <span>
                                              {selectedValues.join('، ')}
                                            </span>
                                          </Typography.Text>
                                        ),
                                      )
                                    : null}
                                </React.Fragment>
                              </Flex>
                            </Col>
                            <Col span={24}>
                              <Divider dashed className='my-0' />
                            </Col>
                            <Col span={24} md={12}>
                              <Form.Item<FieldType>
                                aria-label='قیمت واحد'
                                name={`product-main-price-${id}`}
                                className='mb-0'
                              >
                                <DecimalInputNumber
                                  readOnly
                                  variant='filled'
                                  placeholder='0'
                                  addonBefore='قیمت واحد'
                                  addonAfter={PRICE}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={24} md={12}>
                              <Form.Item<FieldType>
                                aria-label='تخفیف واحد'
                                name={`product-unit-discount-${id}`}
                                className='mb-0'
                              >
                                <DecimalInputNumber
                                  readOnly
                                  variant='filled'
                                  placeholder='0'
                                  addonBefore='تخفیف واحد'
                                  addonAfter={PRICE}
                                />
                              </Form.Item>
                            </Col>
                            <React.Fragment>
                              {hasMultipleAmount ? (
                                <React.Fragment>
                                  <Col span={24} md={12}>
                                    <Form.Item<FieldType>
                                      aria-label='قیمت نهایی'
                                      name={`product-total-price-${id}`}
                                      className='mb-0'
                                    >
                                      <DecimalInputNumber
                                        readOnly
                                        variant='filled'
                                        placeholder='0'
                                        addonBefore='قیمت نهایی'
                                        addonAfter={PRICE}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col span={24} md={12}>
                                    <Form.Item<FieldType>
                                      aria-label='تخفیف نهایی'
                                      name={`product-total-discount-${id}`}
                                      className='mb-0'
                                    >
                                      <DecimalInputNumber
                                        readOnly
                                        variant='filled'
                                        placeholder='0'
                                        addonBefore='تخفیف نهایی'
                                        addonAfter={PRICE}
                                      />
                                    </Form.Item>
                                  </Col>
                                </React.Fragment>
                              ) : null}
                            </React.Fragment>
                            <Col span={24} md={12}>
                              <Form.Item<FieldType>
                                aria-label='حجم سفارش'
                                name={`product-total-size-${id}`}
                                className='mb-0'
                              >
                                <DecimalInputNumber
                                  readOnly
                                  variant='filled'
                                  placeholder='0'
                                  addonBefore='حجم سفارش'
                                  addonAfter={measurementUnitName}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={24} md={12} className='relative'>
                              <Form.Item<FieldType>
                                aria-label='اندازه سفارش'
                                name={`product-total-amount-${id}`}
                                className='mb-0'
                              >
                                <IntegerInputNumber
                                  disabled={isSubProductDeleted}
                                  placeholder='0'
                                  min={0}
                                  max={maxAmount}
                                  addonBefore='اندازه سفارش'
                                  addonAfter={packagingModelLabel}
                                  onChange={value => {
                                    const formattedValue = Number(value ?? 0);
                                    debounceUpdatePurchaseProducts(
                                      id,
                                      formattedValue,
                                    );
                                  }}
                                />
                              </Form.Item>
                              <React.Fragment>
                                {hasPricesDifference ? (
                                  <Button
                                    color='default'
                                    variant='filled'
                                    size='small'
                                    shape='round'
                                    className='absolute -start-0.5 -top-1.5 size-[18px] border-[--ant-color-border] p-0'
                                    icon={
                                      <UndoOutlinedIcon className='text-[10px]' />
                                    }
                                    onClick={() => {
                                      debounceUpdatePurchaseProducts(
                                        id,
                                        maxAmount,
                                      );
                                    }}
                                  >
                                    <span className='sr-only'>
                                      بازنشانی تغییر
                                    </span>
                                  </Button>
                                ) : null}
                              </React.Fragment>
                            </Col>
                            <Col span={24}>
                              <Divider dashed className='my-0' />
                            </Col>
                            <Col span={12}>
                              <Typography.Text type='secondary'>
                                اختلاف قیمت:
                              </Typography.Text>
                              <Typography.Text
                                type={
                                  hasPricesDifference ? 'danger' : undefined
                                }
                                className='ms-1'
                              >
                                {numberFormat(pricesDifference)}
                              </Typography.Text>
                              <Typography.Text> {PRICE}</Typography.Text>
                            </Col>
                            <Col span={12}>
                              <Tag
                                icon={
                                  freeShipping ? (
                                    <CheckOutlined />
                                  ) : (
                                    <CloseOutlined />
                                  )
                                }
                                className={`py-0.5 ${freeShipping ? 'opacity-100' : 'opacity-50'}`}
                                color={freeShipping ? 'blue' : 'error'}
                              >
                                ارسال رایگان
                              </Tag>
                              <Tag
                                icon={
                                  expressShipping ? (
                                    <CheckOutlined />
                                  ) : (
                                    <CloseOutlined />
                                  )
                                }
                                className={`py-0.5 ${expressShipping ? 'opacity-100' : 'opacity-50'}`}
                                color={expressShipping ? 'blue' : 'error'}
                              >
                                ارسال فوری
                              </Tag>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    }
                  />
                </PrimitiveListItem>
              );
            }}
          />
        </Flex>
      </Col>
      <Col span={24}>
        <Divider dashed />
      </Col>
      <Col span={24}>
        <Form.Item<FieldType> name='invoiceInfo'>
          <Descriptions
            bordered
            column={matchesLg ? 4 : undefined}
            layout='vertical'
            size='middle'
            title={
              <Typography.Title level={4} className='mb-0 text-lg'>
                جزئیات فاکتور
              </Typography.Title>
            }
            items={[
              {
                key: randomId(),
                label: 'قیمت محصول‌ها',
                children: (
                  <Space wrap size={2.5}>
                    {hasModifiedProductPrice(
                      initialPurchaseProductsOriginalPrice,
                      purchaseProductsOriginalPrice,
                    ) ? (
                      <Typography.Text delete>
                        {numberFormat(initialPurchaseProductsOriginalPrice)}
                      </Typography.Text>
                    ) : null}
                    <Typography.Text>
                      {numberFormat(purchaseProductsOriginalPrice)}
                    </Typography.Text>
                    <Typography.Text>{PRICE}</Typography.Text>
                  </Space>
                ),
              },
              {
                key: randomId(),
                label: 'تخفیف محصولات',
                children: purchaseProductsDiscount ? (
                  <Space wrap size={2}>
                    <Typography.Text>
                      {numberFormat(purchaseProductsDiscount)}
                    </Typography.Text>
                    <Typography.Text>{PRICE}</Typography.Text>
                  </Space>
                ) : (
                  <Typography.Text disabled>فاقد تخفیف محصولات</Typography.Text>
                ),
              },
              {
                key: randomId(),
                label: 'تخفیف درگاه پرداخت',
                children: gatewayDiscount ? (
                  <Space wrap size={2}>
                    <Typography.Text>
                      {numberFormat(gatewayDiscount)}
                    </Typography.Text>
                    <Typography.Text>{PRICE}</Typography.Text>
                  </Space>
                ) : (
                  <Typography.Text disabled>
                    فاقد تخفیف درگاه پرداخت
                  </Typography.Text>
                ),
              },
              {
                key: randomId(),
                label: 'کد تخفیف',
                children:
                  discountCodeId || discount ? (
                    <Space wrap size={6}>
                      {discountCodeId ? (
                        <AntdRouterExternalLink
                          href={`${DISCOUNT_CODES_BASE_URL}${DISCOUNT_CODES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${discountCodeId}${DEFAULT_SLUG_SEPARATOR}`}
                        >
                          {discountCodeValue}
                        </AntdRouterExternalLink>
                      ) : null}
                      {discount ? (
                        <Space wrap size={2}>
                          <Typography.Text type='secondary'>
                            {numberFormat(discount)}
                          </Typography.Text>
                          <Typography.Text type='secondary'>
                            {PRICE}
                          </Typography.Text>
                        </Space>
                      ) : null}
                    </Space>
                  ) : (
                    <Typography.Text disabled>فاقد کد تخفیف</Typography.Text>
                  ),
              },
              {
                key: randomId(),
                label: 'هزینه ارسال',
                children: deliveryPrice ? (
                  <Space wrap size={2}>
                    <Typography.Text>
                      {numberFormat(deliveryPrice)}
                    </Typography.Text>
                    <Typography.Text>{PRICE}</Typography.Text>
                  </Space>
                ) : (
                  <Typography.Text disabled>فاقد هزینه ارسال</Typography.Text>
                ),
              },
              {
                key: randomId(),
                label: 'درگاه پرداخت',
                children: paymentGatewayId ? (
                  <AntdRouterExternalLink
                    href={`${PAYMENT_GATEWAYS_BASE_URL}${PAYMENT_GATEWAYS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${paymentGatewayId}${DEFAULT_SLUG_SEPARATOR}`}
                  >
                    {paymentGatewayTitle}
                  </AntdRouterExternalLink>
                ) : (
                  <Typography.Text disabled>فاقد درگاه پرداخت</Typography.Text>
                ),
              },
              {
                key: randomId(),
                label: 'مبلغ نهایی',
                children: (
                  <Space wrap size={4}>
                    {hasModifiedProductPrice(
                      initialTotalProductsPrice,
                      totalProductsPrice,
                    ) ? (
                      <Typography.Text delete>
                        {numberFormat(initialTotalProductsPrice)}
                      </Typography.Text>
                    ) : null}
                    <Typography.Text className='text-[18px] font-medium'>
                      {numberFormat(totalProductsPrice)}
                    </Typography.Text>
                    <Typography.Text>{PRICE}</Typography.Text>
                  </Space>
                ),
              },
              {
                key: randomId(),
                label: 'فاکتور کاغذی',
                children: paperInvoice ? (
                  <CheckCircleOutlinedIcon className='text-base text-green-500' />
                ) : (
                  <CloseCircleOutlinedIcon className='text-base text-red-500' />
                ),
              },
              {
                key: randomId(),
                label: 'تاریخ سفارش',
                children: createdDate ? (
                  <bdi>{dateTimeFormat(new Date(createdDate))}</bdi>
                ) : (
                  <Typography.Text disabled>فاقد تاریخ</Typography.Text>
                ),
              },
              {
                key: randomId(),
                label: 'تاریخ آخرین ویرایش',
                children: lastModifiedDate ? (
                  <bdi>{dateTimeFormat(new Date(lastModifiedDate))}</bdi>
                ) : (
                  <Typography.Text disabled>فاقد تاریخ</Typography.Text>
                ),
              },
              {
                key: randomId(),
                label: 'یادداشت',
                children: description ? (
                  <Typography.Text copyable>{description}</Typography.Text>
                ) : (
                  <Typography.Text disabled>فاقد یادداشت</Typography.Text>
                ),
              },
            ]}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export { PurchaseBaseInfo };
